// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.
//
// Label/value shape rendered inside an NCStatTile (nc-components/usage-notes/
// stat-tile-grid.md, "Tile content" — plain spans, not a <dl>/<dt>/<dd> triple,
// since the tile grid itself is the record's structure now). Sizing matches
// AU0005's own tile content (DeviceInfo/MonitoringAgainst in
// LiveMonitorScreen.tsx): a plain, sentence-case text-nc-1 label — no
// uppercase/tracking treatment — under a text-nc-1 font-nc-1 value, both at
// the same regular weight. Do not scale either up "for emphasis"; the tile
// boundary + icon/tone (where used) carry that, not a bigger/bolder label.

import { Check, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { isUnknown, type Unknownable } from "@/contracts/display";
import { cn } from "@/lib/utils";

/**
 * One label/value pair. A value that is unknown renders the literal "unknown" in
 * a muted style (ADR-004 D4 / README) — never hidden, never blank, never faked.
 */
export function Field({
  label,
  value,
  mono = false,
  copyable = false,
}: {
  label: string;
  value: Unknownable<string>;
  /** Monospace the value — for identifier-shaped fields (serial/software/hardware IDs). */
  mono?: boolean;
  /**
   * Device identity boxes only: reveal a copy-to-clipboard affordance on
   * hover that copies exactly this value (never the label). Withheld for an
   * unknown value — there is nothing real to copy.
   */
  copyable?: boolean;
}) {
  const unknown = isUnknown(value);
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center justify-between gap-nc-2">
        <span className="text-nc-1 leading-nc-2 text-muted-foreground">{label}</span>
        {copyable && !unknown ? <CopyValueButton value={value as string} /> : null}
      </div>
      <span
        className={cn(
          "break-words text-nc-1 font-nc-1 leading-nc-2",
          mono && !unknown ? "font-mono" : "",
          unknown ? "italic text-muted-foreground" : "text-foreground",
        )}
      >
        {unknown ? "unknown" : (value as string)}
      </span>
    </div>
  );
}

type CopyStatus = "idle" | "copied" | "fading";

/**
 * Hover-revealed copy-to-clipboard button for a `Field`'s value (identity
 * boxes only, via `copyable`). Copies exactly the rendered value string.
 * The plain copy icon only shows on hover of the enclosing NCStatTile
 * (`group-hover/tile` — see stat-tile-grid.tsx); the post-click feedback
 * (green check) is a separate, hover-independent state so it flashes and
 * fades on its own timer even if the pointer has already left the tile.
 */
function CopyValueButton({ value }: { value: string }) {
  const [status, setStatus] = useState<CopyStatus>("idle");
  const timers = useRef<number[]>([]);

  useEffect(() => {
    return () => {
      timers.current.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard permission denied/unavailable — no destructive fallback, just no-op.
      return;
    }
    timers.current.forEach((id) => window.clearTimeout(id));
    setStatus("copied");
    timers.current = [
      window.setTimeout(() => setStatus("fading"), 1200),
      window.setTimeout(() => setStatus("idle"), 1600),
    ];
  }

  const feedback = status !== "idle";

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={status === "copied" ? "Copied to clipboard" : "Copy value to clipboard"}
      title={status === "copied" ? "Copied" : "Copy"}
      className={cn(
        "inline-flex size-4 shrink-0 items-center justify-center rounded-full transition-opacity duration-300 hover:bg-muted",
        feedback
          ? cn("text-nc-success", status === "fading" ? "opacity-0" : "opacity-100")
          : "text-muted-foreground opacity-0 hover:text-foreground group-hover/tile:opacity-100",
      )}
    >
      {feedback ? (
        <Check className="size-3.5" aria-hidden />
      ) : (
        <Copy className="size-3.5" aria-hidden />
      )}
    </button>
  );
}
